import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import Box from '@mui/material/Box'
import {JsonObject} from 'utilities/types'

import {ISubscription, ITenant} from 'app/models/tenant'
import {CSubscription, CTenant, CUser} from 'app/modules/discovery/core'
import {useMemo, useState} from 'react'

import {DialogAddUserToSubscription} from './dialogAddUserToSubscription'
import {DialogEditSubscription} from './dialogEditSubscription'
import {DialogEditTenant} from './dialogEditTenant'
import {usePropertyEditor} from './usePropertyEditor'

import {api2Endpoint} from 'app/config/apiConfig'
import {ParentContext} from 'app/hooks/ApiParentContext'
import {SubscriptionRepo} from 'app/hooks/api_repos'
import {useOnActionDelete} from 'app/hooks/common/useOnActionDelete'
import {useOnActionPost} from 'app/hooks/common/useOnActionPost'
import {useOnActionPut} from 'app/hooks/common/useOnActionPut'
import {useOnLoadGet} from 'app/hooks/common/useOnLoadGet'
import axios from 'axios'
import React from 'react'
import AdminWrapper from './AdminWrapper'

interface confirmationProps {
  title: string | null
  message: string | null
  action: () => void
}

function IscoutAdminPage() {
  const tenants = useOnLoadGet<any>(`std/tenant`, 'tenants')
  const createTenant = useOnActionPost()
  const deleteTenant = useOnActionDelete()
  //Updating is not working on the tenant (wasn't working with TenantRepo either, both submitting PUT with id, name)
  const updateTenant = useOnActionPut()

  const createSubscription = SubscriptionRepo.useCreate()
  //const createSubscription = useOnActionPost()
  const deleteSubscription = SubscriptionRepo.useDelete()
  const updateSubscription = SubscriptionRepo.useUpdate()
  const removeUserFromSub = useOnActionDelete()
  const resendPass = useOnActionPost()

  const [addUserSubscriptionId, setAddUserSubscriptionId] = useState<string | undefined>(undefined)
  const [addUserTenantId, setAddUserTenantId] = useState<string | undefined>(undefined)

  const [tenantFilter, setTenantFilter] = useState<string>('')
  const [tenantTypeFilter, setTenantTypeFilter] = useState<'personal' | 'organizational' | 'any'>(
    'organizational'
  )

  const tenantEditor = usePropertyEditor<ITenant>({
    props: {name: 'string', description: 'string'},
    action: (id, update) => {
      if (id) {
        updateTenant.mutateAsync({
          route: `std/tenant/${id}`,
          body: {...(update as JsonObject & ITenant), id: id},
        })
      } else {
        if (update.is_personal === undefined) {
          update.is_personal = false
        }
        createTenant.mutateAsync({route: `std/tenant`, data: update as JsonObject & ITenant})
      }
    },
    related: {},
  })

  const subscriptionEditor = usePropertyEditor<ISubscription, {tenant: ITenant | null}>({
    props: {
      name: 'string',
      is_tenant_admin: 'boolean',
      max_users: 'number',
      package_id: 'string',
      allows_delegation: 'boolean',
    },
    action: async (id, update, related) => {
      if (!related.tenant?.id) {
        throw new Error(`Cannot create a subscription without a tenant.`)
      }
      if (id) {
        await updateSubscription.mutateAsync({
          data: update as Partial<ISubscription> & JsonObject,
          id,
          parentContext: new ParentContext({
            parentClass: CTenant,
            childClass: CSubscription,
            parentId: related.tenant.id,
          }),
        })
      } else {
        ;(update as JsonObject).tenant_id = related.tenant.id
        await createSubscription.mutateAsync({
          data: update as ISubscription,
          parentContext: new ParentContext({
            parentClass: CTenant,
            childClass: CSubscription,
            parentId: related.tenant.id,
          }),
        })
      }
    },
    related: {tenant: null},
  })

  const [confirmation, setConfirmation] = useState<confirmationProps>({
    title: null,
    message: null,
    action: () => {},
  })

  const tenantTypeLabels = {
    personal: 'Personal',
    organizational: 'Organizational',
    any: '(all types)',
  }

  const filteredTenants = tenants.data?.filter((tenant: any) => {
    if (tenantFilter && !tenant.name?.toLowerCase().includes(tenantFilter.toLowerCase())) {
      return false
    }
    if (tenantTypeFilter === 'personal' && !tenant.is_personal) {
      return false
    }
    if (tenantTypeFilter === 'organizational' && tenant.is_personal) {
      return false
    }
    return true
  })

  const showConfirm = (title: string, message: string, action: () => void) => {
    setConfirmation({
      title: title,
      message: message,
      action: action,
    })
  }

  const doConfirm = () => {
    const action = confirmation.action
    setConfirmation({
      title: null,
      message: null,
      action: () => {},
    })
    action()
  }

  const doCancel = () => {
    setConfirmation({
      title: null,
      message: null,
      action: () => {},
    })
  }

  const doEditTenant = (event: any, tenant: CTenant | null) => {
    event.stopPropagation()
    tenantEditor.start(tenant ?? new CTenant({id: '', name: '', is_personal: false}), {})
  }

  const doDeleteTenant = (event: any, tenant: CTenant) => {
    event.stopPropagation()
    showConfirm('Delete Tenant', `Delete tenant "${tenant.name}?"`, () => {
      if (!tenant.id) {
        throw new Error(`Cannot delete tenant without ID.`)
      }
      deleteTenant.mutateAsync({route: `std/tenant/${tenant.id}`})
    })
  }

  const doEditSubscription = (event: any, sub: CSubscription | null, tenant: CTenant) => {
    event.stopPropagation()
    const newTenant = new CTenant(tenant as unknown as ITenant & JsonObject)
    subscriptionEditor.start(
      sub ?? new CSubscription({name: '', tenant: newTenant.export(), is_tenant_admin: false}),
      {
        tenant,
      }
    )
  }

  const doDeleteSub = (event: any, sub: CSubscription, tenant: CTenant) => {
    event.stopPropagation()
    showConfirm(
      'Delete Subscription',
      `Delete subscription "${sub.name}" from tenant "${tenant.name}?"`,
      () => {
        if (!sub.id || !tenant.id) {
          throw new Error(`Cannot delete subscription without subscription and tenant ID.`)
        }
        deleteSubscription.mutateAsync({
          id: sub.id,
          parentContext: new ParentContext({
            parentClass: CTenant,
            childClass: CSubscription,
            prop: 'subscriptions',
            parentId: tenant.id,
          }),
        })
      }
    )
  }

  const doAddUsersToSub = (event: any, sub: CSubscription, tenant: CTenant) => {
    event.stopPropagation()
    setAddUserSubscriptionId(sub.id)
    setAddUserTenantId(tenant.id)
  }
  const doResendPasswordEmail = async (
    event: any,
    user: CUser,
    sub: CSubscription,
    tenant: CTenant
  ) => {
    event.stopPropagation()
    if (!user.id || !sub.id || !tenant?.id) {
      throw new Error(`Cannot resend email without user, subscription and tenant ID.`)
    }
    await resendPass.mutateAsync({
      route: `std/user/${user.id}/resend_initial_password`,
      data: undefined,
    })
    alert('Initial password re-sent.')
  }

  const doRemoveUsersFromSub = (event: any, user: CUser, sub: CSubscription, tenant: CTenant) => {
    event.stopPropagation()
    if (!user.id || !sub.id || !tenant?.id) {
      throw new Error(
        `Cannot add/remove users from subscriptions without user, subscription and tenant ID.`
      )
    }
    removeUserFromSub.mutateAsync({route: `std/user/${user.id}/subscriptions/${sub.id}`})
  }

  const usersBySubscriptionId = useMemo(() => {
    const output: {[sub_id: string]: CUser[]} = {}
    if (tenants.data) {
      for (const tenant of tenants.data) {
        if (tenant.subscriptions) {
          for (const subscription of tenant.subscriptions) {
            if (subscription.id && subscription.users) {
              output[subscription.id] = []
              for (const user of subscription.users) {
                if (!user.id) {
                  continue
                }
                if (user.id in output[subscription.id].map((x) => x.id)) {
                  continue
                }
                output[subscription.id].push(user)
              }
            }
          }
        }
      }
    }
    console.log(JSON.stringify(output, null, 2))
    return output
  }, [SubscriptionRepo.timestamp])

  const closeUserEditor = () => {
    setAddUserSubscriptionId(undefined)
    setAddUserTenantId(undefined)
  }

  const accordionMemo = React.useMemo(() => {
    const accord = (
      <Box sx={{margin: '10px'}}>
        {filteredTenants?.map((tenant: any) => {
          return (
            <Accordion key={`tenant-${tenant.id}`}>
              <AccordionSummary
                sx={{display: 'flex', backgroundColor: 'rgb(14,97,176)'}}
                expandIcon={<ExpandMoreIcon sx={{color: 'white'}} />}
              >
                <Box
                  sx={{
                    flex: '1 1 auto',
                    display: 'flex',
                    fontWeight: 'bold',
                    fontSize: '18px',
                    color: 'white',
                  }}
                >
                  <Box sx={{flex: '0 0 auto'}}>{tenant.name}</Box>

                  <Box
                    sx={{
                      fontSize: '14px',
                      padding: '1px 6px',
                      backgroundColor: 'white',
                      color: 'rgb(32, 137, 238)',
                      height: '24px',
                      marginLeft: '18px',
                      fontVariantCaps: 'small-caps',
                      flex: '0 0 auto',
                    }}
                  >
                    {tenant.is_personal ? 'Personal' : 'Organizational'}
                  </Box>
                </Box>
                {tenant.is_personal ? (
                  ''
                ) : (
                  <Box sx={{flex: '0 0 auto', display: 'flex', color: 'white'}}>
                    <Button
                      color='inherit'
                      sx={{fontSize: '14px'}}
                      onClick={(event) => {
                        doEditTenant(event, tenant)
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      color='inherit'
                      sx={{fontSize: '14px'}}
                      onClick={(event) => {
                        doDeleteTenant(event, tenant)
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{display: 'flex'}}>
                  <Box
                    sx={{
                      fontSize: '18px',
                      marginBottom: '10px',
                      fontVariantCaps: 'small-caps',
                      flex: '1 1 auto',
                    }}
                  >
                    Subscriptions
                  </Box>
                  {tenant.is_personal ? (
                    <></>
                  ) : (
                    <Box sx={{flex: '0 0 auto', marginRight: '36px'}}>
                      <Button
                        onClick={(event) => {
                          doEditSubscription(event, null, tenant)
                        }}
                      >
                        Create Subscription
                      </Button>
                    </Box>
                  )}
                </Box>
                {tenant.subscriptions?.map((sub: any) => {
                  const subUsers = usersBySubscriptionId[sub.id ?? '']
                  return (
                    <Accordion disableGutters elevation={0} square key={`sub-${sub.id}`}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{display: 'flex', width: '100%'}}>
                          <Box sx={{flex: '1 1 auto', display: 'flex'}}>
                            <Box
                              sx={{
                                fontSize: '16px',
                                marginRight: '6px',
                                fontWeight: 'bold',
                              }}
                            >
                              {sub.name}
                            </Box>
                            <Box sx={{fontSize: '16px', color: '#888'}}>
                              ({subUsers?.length || 'no'} {subUsers?.length == 1 ? 'user' : 'users'}
                              )
                            </Box>
                            {sub.is_tenant_admin ? (
                              <Box
                                sx={{
                                  fontSize: '14px',
                                  padding: '1px 6px',
                                  backgroundColor: '#884',
                                  color: 'white',
                                  height: '24px',
                                  marginLeft: '12px',
                                  fontVariantCaps: 'small-caps',
                                }}
                              >
                                Admin
                              </Box>
                            ) : (
                              ''
                            )}
                          </Box>
                          {tenant.is_personal ? (
                            ''
                          ) : (
                            <Box sx={{flex: '0 0 auto', display: 'flex'}}>
                              <Box sx={{flex: '0 0 auto'}}>
                                <Button onClick={(event) => doEditSubscription(event, sub, tenant)}>
                                  Edit
                                </Button>
                              </Box>
                              <Box sx={{flex: '0 0 auto'}}>
                                <Button onClick={(event) => doAddUsersToSub(event, sub, tenant)}>
                                  Add Users
                                </Button>
                              </Box>
                              <Box sx={{flex: '0 0 auto'}}>
                                <Button onClick={(event) => doDeleteSub(event, sub, tenant)}>
                                  Delete
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {subUsers?.map((user) => {
                          return (
                            <Box
                              key={`user-${user.id}`}
                              sx={{
                                display: 'flex',
                                borderBottom: '1px solid #eee',
                                paddingBottom: '5px',
                                marginBottom: '5px',
                                '&:last-child': {
                                  borderBottom: '0',
                                  marginBottom: '0',
                                  paddingBottom: '0',
                                },
                              }}
                            >
                              <Box sx={{display: 'flex', flex: '1 1 auto'}}>
                                <Box sx={{minWidth: '250px'}}>
                                  <Box sx={{fontSize: '10px', color: '#888'}}>email</Box>
                                  <Box sx={{fontSize: '14px'}}>{user.email}</Box>
                                </Box>
                                <Box sx={{minWidth: '200px'}}>
                                  <Box sx={{fontSize: '10px', color: '#888'}}>last name</Box>
                                  <Box sx={{fontSize: '14px'}}>{user.last_name}</Box>
                                </Box>
                                <Box sx={{minWidth: '200px'}}>
                                  <Box sx={{fontSize: '10px', color: '#888'}}>first name</Box>
                                  <Box sx={{fontSize: '14px'}}>{user.first_name}</Box>
                                </Box>
                              </Box>
                              <Box sx={{flex: '0 0 auto'}}>
                                <Button
                                  onClick={(event) =>
                                    doResendPasswordEmail(event, user, sub, tenant)
                                  }
                                >
                                  Resend Email
                                </Button>
                              </Box>
                              {tenant.is_personal ? (
                                ''
                              ) : (
                                <Box sx={{flex: '0 0 auto'}}>
                                  <Button
                                    onClick={(event) =>
                                      doRemoveUsersFromSub(event, user, sub, tenant)
                                    }
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          )
                        })}
                      </AccordionDetails>
                    </Accordion>
                  )
                })}
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    )
    return accord
  }, [
    tenants,
    filteredTenants,
    usersBySubscriptionId,
    doEditTenant,
    doDeleteTenant,
    doRemoveUsersFromSub,
    doResendPasswordEmail,
    doAddUsersToSub,
    doDeleteSub,
    doEditSubscription,
  ])

  const sendTestEmail = async () => {
    const res = await axios.get(api2Endpoint('std/user/testemail'))
  }

  //tenant invitation
  const sendTestHTMLEmail = async () => {
    const res = await axios.get(api2Endpoint('std/user/testemailraw'))
  }

  //delegate invitation
  const sendTestEmailDelegation = async () => {
    const res = await axios.get(api2Endpoint('std/user/testemaildelegation'))
  }

  return (
    <AdminWrapper>
      <Button onClick={() => sendTestEmail()}>Send Test Email</Button>
      <Button onClick={() => sendTestHTMLEmail()}>Send Test Tenant Invitation Email</Button>
      <Button onClick={() => sendTestEmailDelegation()}>
        Send Test Delegation Invitation Email
      </Button>
      <Box sx={{backgroundColor: 'rgb(218, 235, 252)'}} m={0} p={0}>
        <Dialog open={!!(confirmation.title && confirmation.message)}>
          <DialogTitle sx={{backgroundColor: 'black', color: 'white'}}>
            {confirmation.title}
          </DialogTitle>
          <Box sx={{margin: '20px', minWidth: '500px', fontSize: '16px'}}>
            <Box sx={{marginBottom: '12px'}}>{confirmation.message}</Box>
            <Box sx={{display: 'flex'}}>
              <Box sx={{flex: '1 1 auto'}}></Box>
              <Box sx={{flex: '0 0 auto'}}>
                <Button sx={{fontSize: '14px'}} onClick={doCancel}>
                  Cancel
                </Button>
                <Button sx={{fontSize: '14px'}} onClick={doConfirm}>
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Dialog>
        <DialogAddUserToSubscription
          subscriptionId={addUserSubscriptionId}
          tenantId={addUserTenantId}
          closeFunc={closeUserEditor}
        />
        <DialogEditSubscription editor={subscriptionEditor} />
        <DialogEditTenant editor={tenantEditor} />

        <Box>
          <Box sx={{minHeight: 'calc(100vh - 72px)'}}>
            <Box
              sx={{
                padding: '1em',
                backgroundColor: 'white',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  maxWidth: '1024px',
                  marginLeft: 'calc(max(50vw - 632px, 0px))',
                }}
              >
                <Box sx={{flex: '1 1 auto'}}>
                  <TextField
                    value={tenantFilter}
                    sx={{margin: 1, minWidth: 250}}
                    label='Search in tenant name'
                    onChange={(event) => {
                      setTenantFilter(event.target.value || '')
                    }}
                  ></TextField>
                  <FormControl variant={'standard'} sx={{m: 1, minWidth: 150}}>
                    <InputLabel id='is-personal-label'>Tenant Type</InputLabel>
                    <Select
                      id='is-personal-select'
                      labelId='is-personal-label'
                      value={tenantTypeFilter}
                      onChange={(event) => {
                        setTenantTypeFilter(
                          event.target.value as 'personal' | 'organizational' | 'any'
                        )
                      }}
                    >
                      <MenuItem value='any'>{tenantTypeLabels['any']}</MenuItem>
                      <MenuItem value='personal'>{tenantTypeLabels['personal']}</MenuItem>
                      <MenuItem value='organizational'>
                        {tenantTypeLabels['organizational']}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{flex: '0 0 auto', margin: '10px 10px 0'}}>
                  <Button
                    sx={{fontSize: '16px'}}
                    onClick={(event) => {
                      doEditTenant(event, null)
                    }}
                  >
                    Create Tenant
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box sx={{maxWidth: '1024px', marginLeft: 'calc(max(50vw - 632px, 0px))'}}>
              {/* <Box sx={{margin: '10px'}}>
                {filteredTenants?.map((tenant) => {
                  return (
                    <Accordion key={`tenant-${tenant.id}`}>
                      <AccordionSummary
                        sx={{display: 'flex', backgroundColor: 'rgb(14,97,176)'}}
                        expandIcon={<ExpandMoreIcon sx={{color: 'white'}} />}
                      >
                        <Box
                          sx={{
                            flex: '1 1 auto',
                            display: 'flex',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            color: 'white',
                          }}
                        >
                          <Box sx={{flex: '0 0 auto'}}>{tenant.name}</Box>

                          <Box
                            sx={{
                              fontSize: '14px',
                              padding: '1px 6px',
                              backgroundColor: 'white',
                              color: 'rgb(32, 137, 238)',
                              height: '24px',
                              marginLeft: '18px',
                              fontVariantCaps: 'small-caps',
                              flex: '0 0 auto',
                            }}
                          >
                            {tenant.is_personal ? 'Personal' : 'Organizational'}
                          </Box>
                        </Box>
                        {tenant.is_personal ? (
                          ''
                        ) : (
                          <Box sx={{flex: '0 0 auto', display: 'flex', color: 'white'}}>
                            <Button
                              color='inherit'
                              sx={{fontSize: '14px'}}
                              onClick={(event) => {
                                doEditTenant(event, tenant)
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              color='inherit'
                              sx={{fontSize: '14px'}}
                              onClick={(event) => {
                                doDeleteTenant(event, tenant)
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        )}
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{display: 'flex'}}>
                          <Box
                            sx={{
                              fontSize: '18px',
                              marginBottom: '10px',
                              fontVariantCaps: 'small-caps',
                              flex: '1 1 auto',
                            }}
                          >
                            Subscriptions
                          </Box>
                          {tenant.is_personal ? (
                            <></>
                          ) : (
                            <Box sx={{flex: '0 0 auto', marginRight: '36px'}}>
                              <Button
                                onClick={(event) => {
                                  doEditSubscription(event, null, tenant)
                                }}
                              >
                                Create Subscription
                              </Button>
                            </Box>
                          )}
                        </Box>
                        {tenant.subscriptions?.map((sub) => {
                          const subUsers = usersBySubscriptionId[sub.id ?? '']
                          return (
                            <Accordion disableGutters elevation={0} square key={`sub-${sub.id}`}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{display: 'flex', width: '100%'}}>
                                  <Box sx={{flex: '1 1 auto', display: 'flex'}}>
                                    <Box
                                      sx={{
                                        fontSize: '16px',
                                        marginRight: '6px',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      {sub.name}
                                    </Box>
                                    <Box sx={{fontSize: '16px', color: '#888'}}>
                                      ({subUsers?.length || 'no'}{' '}
                                      {subUsers?.length == 1 ? 'user' : 'users'})
                                    </Box>
                                    {sub.is_tenant_admin ? (
                                      <Box
                                        sx={{
                                          fontSize: '14px',
                                          padding: '1px 6px',
                                          backgroundColor: '#884',
                                          color: 'white',
                                          height: '24px',
                                          marginLeft: '12px',
                                          fontVariantCaps: 'small-caps',
                                        }}
                                      >
                                        Admin
                                      </Box>
                                    ) : (
                                      ''
                                    )}
                                  </Box>
                                  {tenant.is_personal ? (
                                    ''
                                  ) : (
                                    <Box sx={{flex: '0 0 auto', display: 'flex'}}>
                                      <Box sx={{flex: '0 0 auto'}}>
                                        <Button
                                          onClick={(event) =>
                                            doEditSubscription(event, sub, tenant)
                                          }
                                        >
                                          Edit
                                        </Button>
                                      </Box>
                                      <Box sx={{flex: '0 0 auto'}}>
                                        <Button
                                          onClick={(event) => doAddUsersToSub(event, sub, tenant)}
                                        >
                                          Add Users
                                        </Button>
                                      </Box>
                                      <Box sx={{flex: '0 0 auto'}}>
                                        <Button
                                          onClick={(event) => doDeleteSub(event, sub, tenant)}
                                        >
                                          Delete
                                        </Button>
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                {subUsers?.map((user) => {
                                  return (
                                    <Box
                                      key={`user-${user.id}`}
                                      sx={{
                                        display: 'flex',
                                        borderBottom: '1px solid #eee',
                                        paddingBottom: '5px',
                                        marginBottom: '5px',
                                        '&:last-child': {
                                          borderBottom: '0',
                                          marginBottom: '0',
                                          paddingBottom: '0',
                                        },
                                      }}
                                    >
                                      <Box sx={{display: 'flex', flex: '1 1 auto'}}>
                                        <Box sx={{minWidth: '250px'}}>
                                          <Box sx={{fontSize: '10px', color: '#888'}}>email</Box>
                                          <Box sx={{fontSize: '14px'}}>{user.email}</Box>
                                        </Box>
                                        <Box sx={{minWidth: '200px'}}>
                                          <Box sx={{fontSize: '10px', color: '#888'}}>
                                            last name
                                          </Box>
                                          <Box sx={{fontSize: '14px'}}>{user.last_name}</Box>
                                        </Box>
                                        <Box sx={{minWidth: '200px'}}>
                                          <Box sx={{fontSize: '10px', color: '#888'}}>
                                            first name
                                          </Box>
                                          <Box sx={{fontSize: '14px'}}>{user.first_name}</Box>
                                        </Box>
                                      </Box>
                                      <Box sx={{flex: '0 0 auto'}}>
                                        <Button
                                          onClick={(event) =>
                                            doResendPasswordEmail(event, user, sub, tenant)
                                          }
                                        >
                                          Resend Email
                                        </Button>
                                      </Box>
                                      {tenant.is_personal ? (
                                        ''
                                      ) : (
                                        <Box sx={{flex: '0 0 auto'}}>
                                          <Button
                                            onClick={(event) =>
                                              doRemoveUsersFromSub(event, user, sub, tenant)
                                            }
                                          >
                                            Remove
                                          </Button>
                                        </Box>
                                      )}
                                    </Box>
                                  )
                                })}
                              </AccordionDetails>
                            </Accordion>
                          )
                        })}
                      </AccordionDetails>
                    </Accordion>
                  )
                })}
              </Box> */}
              {accordionMemo}
            </Box>
          </Box>
        </Box>
      </Box>
    </AdminWrapper>
  )
}

export {IscoutAdminPage}
